import { Beer, Building, ChefHat, Hotel, PartyPopper, UtensilsCrossed } from "lucide-react";
import { useState } from "react";

import { useUpdateVenueProfile } from "@/features/venues/model/useVenueProfile";
import type { VenueCategory, VenueProfile } from "@/features/venues/types";
import { cn } from "@/shared/lib/cn";

import { StepFooter } from "@/features/venue-onboarding/ui/StepFooter";

interface Props {
  profile: VenueProfile;
  onSaved: () => void;
  onBack?: () => void;
}

interface Option {
  value: VenueCategory;
  title: string;
  description: string;
  icon: React.ReactNode;
}

const OPTIONS: Option[] = [
  {
    value: "RESTAURANT_DINING",
    title: "Restaurant / dining",
    description: "Sit-down restaurant, cafe, or fine dining.",
    icon: <UtensilsCrossed className="h-5 w-5" />,
  },
  {
    value: "BAR_NIGHTCLUB",
    title: "Bar / nightclub",
    description: "Cocktail bar, pub, club, lounge.",
    icon: <Beer className="h-5 w-5" />,
  },
  {
    value: "EVENT",
    title: "Event venue",
    description: "Wedding, conference, festival, private party.",
    icon: <PartyPopper className="h-5 w-5" />,
  },
  {
    value: "CATERING",
    title: "Catering",
    description: "Off-site catering, corporate, food trucks.",
    icon: <ChefHat className="h-5 w-5" />,
  },
  {
    value: "HOTEL",
    title: "Hotel",
    description: "Hotel restaurant, banquet, room service.",
    icon: <Hotel className="h-5 w-5" />,
  },
  {
    value: "OTHER",
    title: "Something else",
    description: "Doesn't quite fit? Tell us what you are.",
    icon: <Building className="h-5 w-5" />,
  },
];

export function CategoryStep({ profile, onSaved, onBack }: Props) {
  const update = useUpdateVenueProfile();
  const [selected, setSelected] = useState<VenueCategory | null>(profile.category ?? null);
  const [notes, setNotes] = useState<string>(profile.notes ?? "");

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (selected) {
      const payload: { category: VenueCategory; notes?: string } = { category: selected };
      // Capture the "what kind of venue" free-text alongside OTHER so it
      // shows up on shift cards and the venue profile.
      if (selected === "OTHER") {
        payload.notes = notes.trim();
      }
      await update.mutateAsync(payload);
    }
    onSaved();
  };

  return (
    <form onSubmit={onSubmit} className="space-y-5">
      <div>
        <h2 className="text-xl font-semibold tracking-display-tight text-copy">
          What kind of venue are you?
        </h2>
        <p className="mt-1 text-sm text-copy-muted">
          Pick the option that best describes your space. You can change this later.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {OPTIONS.map((option) => {
          const isSelected = selected === option.value;
          return (
            <button
              key={option.value}
              type="button"
              onClick={() => setSelected(option.value)}
              className={cn(
                "group flex items-start gap-3 rounded-lg border bg-surface p-4 text-left transition-colors",
                isSelected
                  ? "border-primary bg-primary-light ring-2 ring-primary"
                  : "border-border hover:border-primary hover:bg-surface-sunken",
              )}
              aria-pressed={isSelected}
            >
              <span
                className={cn(
                  "mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-md transition-colors",
                  isSelected
                    ? "bg-primary text-primary-foreground"
                    : "bg-primary-light text-primary group-hover:bg-primary group-hover:text-primary-foreground",
                )}
              >
                {option.icon}
              </span>
              <div className="min-w-0 space-y-0.5">
                <p className="text-sm font-medium text-copy">{option.title}</p>
                <p className="text-xs text-copy-muted">{option.description}</p>
              </div>
            </button>
          );
        })}
      </div>

      {selected === "OTHER" && (
        <div className="space-y-1">
          <label htmlFor="other_notes" className="text-sm font-medium text-copy">
            Tell us about your venue
          </label>
          <textarea
            id="other_notes"
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            maxLength={1000}
            placeholder="What kind of space is it? Any context workers should know."
            className="w-full rounded-md border border-border bg-surface px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-ring sm:text-sm"
          />
          <p className="text-xs text-copy-muted">Visible to workers. You can edit this anytime from your profile.</p>
        </div>
      )}

      <StepFooter
        onBack={onBack}
        isSubmitting={update.isPending}
        nextLabel={selected ? "Continue" : "Skip for now"}
      />
    </form>
  );
}
